import { useEffect, useMemo, useState } from "react";
import { analyzeProject } from "@buildlab/codeview";
import { buildPreview, PREVIEW_SANDBOX } from "@buildlab/preview";
import { api, type PastTurn, type TeacherProjectView, lessonLabel } from "./api";
import { CodeCards } from "./CodeCards";
import { Editor } from "./Editor";
import { Back, Eye, Restart } from "./icons";
import { ErrorScreen, LoadingScreen } from "./Lion";
import "./LiveView.css";

// A student's project for the teacher, read-only (Q): the running preview, the files and Cards, and
// every message to the helper. Nothing here can change the student's work. It refreshes every 10 s
// so the teacher can watch along; the preview only restarts when the code actually changed.

const POLL_MS = 10_000;
const ORDER = ["index.html", "style.css", "game.js", "PROJECT.md"];
const sortFiles = (names: string[]) => [...names].sort((a, b) => (ORDER.indexOf(a) + 1 || 99) - (ORDER.indexOf(b) + 1 || 99) || a.localeCompare(b));
const MODE_NAMES: Record<string, string> = { build: "Build", plan: "Plan", ask: "Ask" };
const noop = () => {};

export function LiveProject({ projectId, onBack, backLabel = "Live view" }: { projectId: string; onBack: () => void; backLabel?: string }) {
  const [data, setData] = useState<TeacherProjectView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"cards" | "files">("files");
  const [file, setFile] = useState<string | null>(null);
  const [run, setRun] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api.teacherProject(projectId).then(
        (r) => alive && setData(r),
        (e: Error) => alive && setError(e.message),
      );
    void load();
    const timer = window.setInterval(() => document.visibilityState === "visible" && void load(), POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [projectId]);

  const files = data?.project.files;
  const html = useMemo(() => (files ? buildPreview(files).html : ""), [JSON.stringify(files ?? {})]);
  const views = useMemo(() => (files ? analyzeProject(files) : []), [html]);

  if (error && !data) return <ErrorScreen message={error} />;
  if (!data || !files) return <LoadingScreen text="Opening the student's project…" />;
  const names = sortFiles(Object.keys(files));
  const open = file && files[file] !== undefined ? file : (names.find((n) => n.endsWith(".js")) ?? names[0]!);

  return (
    <div className="live-project">
      <header className="gbar">
        <button type="button" className="pill-dark back" onClick={onBack}>
          <Back />
          {backLabel}
        </button>
        <div className="title">
          <span className="eyebrow gold">{data.student.name}</span>
          <h1>{data.assignment ? `${lessonLabel(data.assignment.lesson)} · ${data.project.title}` : data.project.title}</h1>
        </div>
        {data.helperRunning && <span className="lt-running">Helper is working…</span>}
        <span className="lp-readonly">
          <Eye size={14} />
          Read-only
        </span>
      </header>

      <div className="lpj-body">
        <section className="lpj-panel lpj-left" aria-label="Their code">
          <div className="tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === "files"} onClick={() => setTab("files")}>
              Files
            </button>
            <button type="button" role="tab" aria-selected={tab === "cards"} onClick={() => setTab("cards")}>
              Cards
            </button>
          </div>
          {tab === "cards" ? (
            <div className="lpj-cards">
              <CodeCards views={views} labels={{}} watch={null} errors={[]} disabled picked={null} onPick={noop} onChange={noop} onAsk={noop} onShowCode={noop} />
            </div>
          ) : (
            <div className="code">
              <div className="code-tabs">
                {names.map((n) => (
                  <button key={n} type="button" className={n === open ? "on" : ""} onClick={() => setFile(n)}>
                    {n}
                  </button>
                ))}
              </div>
              <Editor key={open} path={open} value={files[open]!} readOnly onChange={noop} />
            </div>
          )}
        </section>

        <section className="lpj-panel lpj-preview" aria-label="Their project, running">
          <div className="row between">
            <span className="eyebrow">Preview · as it is now</span>
            <button type="button" className="ghost" onClick={() => setRun((n) => n + 1)}>
              <Restart size={14} />
              Restart
            </button>
          </div>
          <iframe key={run} title="Student project preview" sandbox={PREVIEW_SANDBOX} srcDoc={html} />
        </section>

        <section className="lpj-panel lpj-transcript" aria-label="Messages to the helper">
          <div className="row between">
            <span className="eyebrow">Messages to the helper</span>
            <span className="muted small">{data.turns.length === 0 ? "none yet" : `last ${data.turns.length}`}</span>
          </div>
          {data.turns.length === 0 && <p className="muted small">They haven't asked the helper for anything in this project yet.</p>}
          <ol className="lpj-turns">
            {[...data.turns].reverse().map((t) => (
              <Turn key={t.id} t={t} />
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
}

function Turn({ t }: { t: PastTurn }) {
  const reply = t.summary.length ? t.summary.join(" ") : t.finalText;
  const time = new Date(t.createdAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return (
    <li>
      <div className="lpj-tmeta">
        <span className={`lt-mode m-${t.mode}`}>{MODE_NAMES[t.mode] ?? t.mode}</span>
        <span className="muted small">{time}</span>
        {!["done", "answer", "plan", "questions"].includes(t.status) && <span className="lpj-status">{t.status.replace("_", " ")}</span>}
      </div>
      <blockquote>{t.message.trim() ? t.message : <i>Approved the helper's plan</i>}</blockquote>
      {t.plan?.steps.length ? (
        <ol className="lpj-plan">
          {t.plan.steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      ) : (
        reply && <p className="lpj-reply">{reply.length > 600 ? `${reply.slice(0, 599)}…` : reply}</p>
      )}
      {t.steps.length > 0 && <p className="lpj-steps">{t.steps.join(" · ")}</p>}
    </li>
  );
}
